import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faArrowLeft } from '@/fontawesome';
import Layout from '@/components/layout/Layout';
import type { Course, CourseID } from '@/models/course';
import { APIRoutes } from '@/services/api.routes';

interface CourseLockedPageProps {
  onLogout: () => void;
}

export default function CourseLockedPage({ onLogout }: CourseLockedPageProps) {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourse = async () => {
      if (courseId === undefined) {
        setError('Keine Kurs-ID angegeben');
        setLoading(false);
        return;
      }

      try {
        const courseData = await APIRoutes.getCourse(courseId as CourseID);
        setCourse(courseData);
      } catch (err) {
        console.error('Error loading course:', err);
        setError('Kurs konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId]);

  return (
    <Layout onLogout={onLogout}>
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
            {loading ? (
              <div className="py-8">
                <p className="text-gray-500 dark:text-gray-400">Lade Kurs...</p>
              </div>
            ) : error !== null ? (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <FontAwesomeIcon
                      icon={faLock}
                      className="w-8 h-8 text-gray-600 dark:text-gray-400"
                    />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Fehler
                  </h1>
                </div>
                <div className="mb-8">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {error}
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <Link
                    to="/courses"
                    className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
                    Zurück zur Kursübersicht
                  </Link>
                </div>
              </>
            ) : course !== null ? (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <FontAwesomeIcon
                      icon={faLock}
                      className="w-8 h-8 text-gray-600 dark:text-gray-400"
                    />
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Kurs gesperrt
                  </h1>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                    {course.title}
                  </p>
                </div>

                <div className="mb-8">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {course.description}
                  </p>
                  <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                    Dieser Kurs ist in Ihrem aktuellen Plan nicht enthalten. Upgraden Sie Ihren
                    Account, um Zugriff zu erhalten.
                  </p>
                </div>

                <div className="space-y-3">
                  <Link
                    to="/pricing"
                    className="block w-full px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors text-center"
                  >
                    Feature freischalten
                  </Link>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <Link
                    to="/courses"
                    className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
                    Zurück zur Kursübersicht
                  </Link>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  );
}
